import type { MaybeRef } from "@vueuse/core";
import type { PerfEval, WellProperties } from "@/composables/forms/formTypes";

import { ref, computed } from "vue";

import {
  Well_Test_Gas_Input_Options_Enum,
  Well_Test_Liquid_Input_Options_Enum
} from "gql/api";

import { convert } from "@/libs/units";
import { GasFlowRateUnits } from "@/libs/units/flowRate";
import { PercentageUnits } from "@/libs/units/percentage";

type BaseFlowRates = Pick<
  NonNullable<WellProperties>,
  | "well_test_gas_rate_mscf_day"
  | "well_test_oil_rate_stb_day"
  | "well_test_water_rate_stb_day"
>;

export const useFlowRates = (
  _baseRates: MaybeRef<BaseFlowRates>,
  _liquidInputType: MaybeRef<
    Well_Test_Liquid_Input_Options_Enum | null | undefined
  >,
  _gasInputType: MaybeRef<Well_Test_Gas_Input_Options_Enum | null | undefined>
) => {
  const baseRates = ref(_baseRates);
  const liquidInputType = ref(_liquidInputType);
  const gasInputType = ref(_gasInputType);

  const newRates = ref({
    well_test_gas_rate_mscf_day: baseRates.value.well_test_gas_rate_mscf_day,
    well_test_oil_rate_stb_day: baseRates.value.well_test_oil_rate_stb_day,
    well_test_water_rate_stb_day: baseRates.value.well_test_water_rate_stb_day
  });

  const gasFlowRateMscfD = computed({
    get() {
      return newRates.value.well_test_gas_rate_mscf_day;
    },
    set(val) {
      newRates.value.well_test_gas_rate_mscf_day = val;
    }
  });

  const oilFlowRateStbD = computed({
    get() {
      return newRates.value.well_test_oil_rate_stb_day;
    },
    set(val) {
      const oldGasOilRatioScfStb = gasOilRatioScfStb.value;
      const oldGasLiquidRatioScfStb = gasLiquidRatioScfStb.value;

      newRates.value.well_test_oil_rate_stb_day = val;

      switch (gasInputType.value) {
        case Well_Test_Gas_Input_Options_Enum.GasOilRatio:
          gasOilRatioScfStb.value = oldGasOilRatioScfStb;
          break;
        case Well_Test_Gas_Input_Options_Enum.GasLiquidRatio:
          gasLiquidRatioScfStb.value = oldGasLiquidRatioScfStb;
          break;
      }
    }
  });

  const waterFlowRateStbD = computed({
    get() {
      return newRates.value.well_test_water_rate_stb_day;
    },
    set(val) {
      const oldGasLiquidRatioScfStb = gasLiquidRatioScfStb.value;

      newRates.value.well_test_water_rate_stb_day = val;

      if (
        gasInputType.value === Well_Test_Gas_Input_Options_Enum.GasLiquidRatio
      ) {
        gasLiquidRatioScfStb.value = oldGasLiquidRatioScfStb;
      }
    }
  });

  const gasFlowRateScfD = computed({
    get() {
      return convert(
        gasFlowRateMscfD.value,
        GasFlowRateUnits.ThousandCubicFeetPerDay,
        GasFlowRateUnits.CubicFeetPerDay
      );
    },
    set(val) {
      gasFlowRateMscfD.value = convert(
        val,
        GasFlowRateUnits.CubicFeetPerDay,
        GasFlowRateUnits.ThousandCubicFeetPerDay
      );
    }
  });

  const safeRatio = (numerator: number, denominator: number): number => {
    if (
      !Number.isFinite(numerator) ||
      !Number.isFinite(denominator) ||
      denominator === 0
    ) {
      return 0;
    }

    const ratio = numerator / denominator;
    return Number.isFinite(ratio) ? ratio : 0;
  };

  const waterCutRatio = computed({
    get() {
      return safeRatio(waterFlowRateStbD.value, totalLiquidFlowRateStbD.value);
    },
    set(val) {
      const clampedWaterCut = Math.min(Math.max(val, 0), 1);

      if (
        liquidInputType.value ===
        Well_Test_Liquid_Input_Options_Enum.OilRateWaterCut
      ) {
        if (clampedWaterCut >= 1) {
          waterFlowRateStbD.value = totalLiquidFlowRateStbD.value;
          oilFlowRateStbD.value = 0;
          return;
        }

        waterFlowRateStbD.value =
          oilFlowRateStbD.value / (1 - clampedWaterCut) -
          oilFlowRateStbD.value;
      } else {
        const oldTotalLiquidFlowRateStbD = totalLiquidFlowRateStbD.value;
        waterFlowRateStbD.value = oldTotalLiquidFlowRateStbD * clampedWaterCut;
        oilFlowRateStbD.value = oldTotalLiquidFlowRateStbD * (1 - clampedWaterCut);
      }
    }
  });

  const waterCutPercentage = computed({
    get() {
      return convert(
        waterCutRatio.value,
        PercentageUnits.Ratio,
        PercentageUnits.Percentage
      );
    },
    set(val) {
      waterCutRatio.value = convert(
        val,
        PercentageUnits.Percentage,
        PercentageUnits.Ratio
      );
    }
  });

  const totalLiquidFlowRateStbD = computed({
    get() {
      return oilFlowRateStbD.value + waterFlowRateStbD.value;
    },
    set(val) {
      const oldWaterCutRatio = waterCutRatio.value;
      waterFlowRateStbD.value = val * oldWaterCutRatio;
      oilFlowRateStbD.value = val * (1 - oldWaterCutRatio);
    }
  });

  const gasOilRatioScfStb = computed({
    get() {
      return safeRatio(gasFlowRateScfD.value, oilFlowRateStbD.value);
    },
    set(val) {
      gasFlowRateScfD.value = val * oilFlowRateStbD.value;
    }
  });

  const gasLiquidRatioScfStb = computed({
    get() {
      return safeRatio(gasFlowRateScfD.value, totalLiquidFlowRateStbD.value);
    },
    set(val) {
      gasFlowRateScfD.value = val * totalLiquidFlowRateStbD.value;
    }
  });

  return {
    oilFlowRateStbD,
    waterFlowRateStbD,
    totalLiquidFlowRateStbD,
    waterCutRatio,
    waterCutPercentage,
    gasFlowRateScfD,
    gasFlowRateMscfD,
    gasLiquidRatioScfStb,
    gasOilRatioScfStb
  };
};
